import { AuthUser, fetchAuthSession, getCurrentUser, signOut } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import { AbilityBuilder } from '@casl/ability';
import { useRouter } from 'next/router';
import { ShiftNames, UserPermission } from 'packages/workspace/modules/algebra/constants';
import { AppAbilities } from 'packages/workspace/modules/helpers/contexts/abilities';
import { configureAmplify } from 'packages/workspace/services/amplify/configure-amplify';
import { useDiscoveryParams, useLogoutLogs } from 'packages/workspace/services/fetchers/workspace-info';
import type { IamPrincipal } from 'packages/workspace/services/models/session';
import { AbilityStore } from 'packages/workspace/services/store/abilities-store';
import { InternalConversationStore } from 'packages/workspace/services/store/omnichannel/internal-conversation-store';
import useSession, { sessionStore } from 'packages/workspace/services/store/session-store';
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import type { IAuthContext } from '../helpers/contexts/auth';
import { Http } from './http';
import { SoundOptions } from 'packages/workspace/services/store/useSoundStore';
import useOnlineStatus from './useOnlineStatus';

export const useAuth = (): IAuthContext => {
  const [cognitoUsr, setCognitoUser] = useState<AuthUser | null>(null);
  const [systmUser, setSystemUser] = useState<IamPrincipal | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [logIn, setLogIn] = useState<boolean>(false);
  const session = useSession();
  const router = useRouter();
  const queryClient = useQueryClient();
  const host = typeof window !== 'undefined' ? window.location.host : router.basePath;
  const online = useOnlineStatus();
  const { data: discoveryParams } = useDiscoveryParams(host);
  if (session.discoveryParams === null && discoveryParams) {
    sessionStore.updateDiscoveryParams(discoveryParams);
    configureAmplify(host, discoveryParams);
  }
  const { mutateAsync: mutateLogoutLogs } = useLogoutLogs();
  useEffect(() => {
    Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signedIn': {
          setLogIn(true);
          sessionStore.setCognitoUser(data);
          setCognitoUser(data);
          break;
        }
        case 'signedOut': {
          setCognitoUser(null);
          setSystemUser(null);
          setLogIn(false);
          sessionStore.clearStore();
          queryClient.clear();
          InternalConversationStore.clearStore();
          break;
        }
        default:
          break;
      }
    });
  }, []);

  const defineUserAbilities = (application_access = {}) => {
    const existingRules = AbilityStore.getAbilities().rules;
    const { can: allow, rules } = new AbilityBuilder(AppAbilities);
    if (application_access.quicklink) allow(UserPermission.QUICKLINK, ShiftNames.USER);
    if (application_access.oncall) allow(UserPermission.ONCALL, ShiftNames.USER);
    if (application_access.internal_oncall_handle) allow(UserPermission.INTERNAL_ONCALL_HANDLE, ShiftNames.USER);
    if (application_access.externalcall) allow(UserPermission.EXTERNAL_CALL, ShiftNames.USER);
    if (application_access.internal_oncall_token) allow(UserPermission.INTERNAL_ONCALL_TOKEN, ShiftNames.USER);
    if (application_access.dashboard_document_list) allow(UserPermission.DASHBOARD_DOCUMENT_LIST, ShiftNames.USER);
    if (application_access.dashboard_content_get) allow(UserPermission.DASHBOARD_CONTENT_GET, ShiftNames.USER);
    return [...existingRules.filter((iRule) => iRule.subject !== ShiftNames.USER), ...rules];
  };

  useEffect(() => {
    async function checkSystemAuth() {
      try {
        const systemUser = await Http.Request<IamPrincipal>('GET', `/workspaces/api/v1/authn.login${logIn ? '?signIn=true' : ''}`);
        if (logIn) {
          await Http.Request<IamPrincipal>('GET', '/workspaces/api/v1/authn.audit');
          setLogIn(false);
        }
        //toDO define and update rules
        const rules = defineUserAbilities(systemUser.application_access);
        AbilityStore.updateRules(rules);

        sessionStore.setAuthUser(systemUser);
        SoundOptions.setStaffChatSoundStatus(systemUser?.security?.mute);
        setSystemUser(systemUser);
      } catch (err) {
        setSystemUser(null);
        setError(true);
        mutateLogoutLogs({ tag: 'useAuth' });
        signOut();
      }
    }
    const currentSession = async () => {
      const { tokens } = await fetchAuthSession();
      if (session.cognitoUser !== null && tokens?.idToken?.toString() !== null && tokens?.idToken?.toString() !== undefined) {
        checkSystemAuth();
      }
    };
    if (online) {
      currentSession();
    }
  }, [session.cognitoUser, online]);

  const checkUserAuth = useCallback(async () => {
    if (session.discoveryParams !== null) {
      try {
        const cognitoUser = await getCurrentUser();
        sessionStore.setCognitoUser(cognitoUser);
        setCognitoUser(cognitoUser);
      } catch (err) {
        queryClient.invalidateQueries();
        router.push('/auth/auth.page');
        setCognitoUser(null);
      }
      setLoading(false);
    }
  }, [session.discoveryParams]);

  /**
   *This is ONLY Used for PAGE REFRESH SCENARIOS
   */
  useEffect(() => {
    checkUserAuth();
  }, [checkUserAuth]);

  return { system_user: systmUser, cognito_user: cognitoUsr, loading, error };
};

export default useAuth;
